'use client';

import type { DayCellContentArg } from '@fullcalendar/core';
import { eventOccursOnCalendarDay } from '@/app/(main)/calendar/utils/eventOccursOnCalendarDay';
import {
  formatBirthdaysAriaLabel,
  formatGroupedBirthdayLabel,
} from '@/app/(main)/calendar/utils/formatGroupedBirthdayLabel';
import type { CalendarEvent } from '@/types/calendarTypes';
import { cn } from '@/utils/сn';
import BirthdayIcon from '/public/icons/birthday.svg';

export type CalendarDayCellContentOptions = {
  birthdayEvents: CalendarEvent[];
  isMobile: boolean;
  onOpenBirthdaysModal: (date: Date, birthdays: CalendarEvent[]) => void;
};

type CalendarDayCellContentProps = CalendarDayCellContentOptions & {
  arg: DayCellContentArg;
};

export function CalendarDayCellContent({
  arg,
  birthdayEvents,
  isMobile,
  onOpenBirthdaysModal,
}: CalendarDayCellContentProps) {
  if (arg.view.type !== 'dayGridMonth') {
    return <>{arg.dayNumberText}</>;
  }

  const dayBirthdays = birthdayEvents.filter((event) => eventOccursOnCalendarDay(event, arg.date));
  const dayNumberClassName = arg.isOther
    ? 'sf-calendar-month-day-number sf-calendar-month-day-number--other'
    : 'sf-calendar-month-day-number';
  const birthdayLabel = formatGroupedBirthdayLabel(dayBirthdays, { countOnly: isMobile });

  const hasCountBadge = isMobile && dayBirthdays.length > 0;

  return (
    <span
      className={cn('sf-calendar-month-day-top-content', {
        'sf-calendar-month-day-top-content--with-count-badge': hasCountBadge,
      })}
    >
      <span className={dayNumberClassName}>{arg.dayNumberText}</span>
      {dayBirthdays.length > 0 ? (
        <button
          type="button"
          className={cn('sf-calendar-month-birthday-badge', {
            'sf-calendar-month-birthday-badge--count-only': isMobile,
          })}
          aria-label={formatBirthdaysAriaLabel(dayBirthdays)}
          onClick={(e) => {
            e.stopPropagation();
            onOpenBirthdaysModal(arg.date, dayBirthdays);
          }}
        >
          <BirthdayIcon className="h-3.5 w-3.5 shrink-0 text-status-success" aria-hidden />
          <span className="sf-calendar-month-birthday-badge__label min-w-0 overflow-hidden text-ellipsis whitespace-nowrap">
            {birthdayLabel}
          </span>
        </button>
      ) : null}
    </span>
  );
}

export function renderCalendarDayCellContent(arg: DayCellContentArg, options: CalendarDayCellContentOptions) {
  return <CalendarDayCellContent arg={arg} {...options} />;
}
